import { ReportShapeItem, ShapeType } from '../../reportsTypes';
import {
  GeoJsonDictionary,
  ShapeDictionaryType,
  StringDictionary,
} from '../../utils';
import { useCallback, useMemo } from 'react';
import { createDynamicShapeZodSchema } from '../utils/createDynamicShapeZodSchema';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface NewDictionary {
  [key: string]: any;
}

export type ReportTemplateShapeValues = z.infer<
  ReturnType<typeof createDynamicShapeZodSchema>
>;

const addMultiShapeItemsToForm = (
  shapeItems: ReportShapeItem[],
  newDictionary: NewDictionary,
  shapeDictionary: GeoJsonDictionary | null
) => {
  if (!shapeDictionary) {
    shapeItems.forEach((shapeItem) => {
      newDictionary[shapeItem.name] = [];
    });
  } else {
    Object.keys(shapeDictionary).forEach((key) => {
      const featureCollection = shapeDictionary[key];
      newDictionary[key] = [];

      if (!featureCollection.features) {
        return;
      }

      if (!Array.isArray(newDictionary[key])) {
        return;
      }

      featureCollection.features.forEach((shape) => {
        const properties = shape.properties;

        if (
          properties &&
          properties.id !== undefined &&
          properties.id !== null
        ) {
          (newDictionary[key] as string[]).push(properties.id);
        }
      });
    });
  }
};

const addItemsToForm = (
  shapeItems: ReportShapeItem[],
  newDictionary: NewDictionary,
  shapeDictionary: ShapeDictionaryType | null
) => {
  if (!shapeDictionary) {
    shapeItems.forEach((shapeItem) => {
      if (shapeItem.name === 'ports' || shapeItem.name === 'port') {
        newDictionary[shapeItem.name] = [];
      } else {
        newDictionary[shapeItem.name] = undefined;
      }
    });
  } else {
    Object.keys(shapeDictionary).forEach((key) => {
      if (key === 'ports' || key === 'port') {
        newDictionary[key] = shapeDictionary[key] || [];
      } else {
        newDictionary[key] = shapeDictionary[key] || undefined;
      }
    });
  }
};

const useExtractedShapeSchema = (
  shapeItems: ReportShapeItem[],
  type: ShapeType,
  instantShape: boolean | undefined
) => {
  return useCallback(
    () => createDynamicShapeZodSchema(shapeItems, type, instantShape),
    [shapeItems, instantShape, type]
  )();
};

export function useFormSetup(
  shapeItems: ReportShapeItem[],
  shapeItemData: ShapeDictionaryType | null,
  type: ShapeType,
  instantShapeData?: GeoJsonDictionary | null,
  instantShape?: boolean
) {
  const shapeSchema = useExtractedShapeSchema(shapeItems, type, instantShape);
  // Converting GeoJson shape to string id when using RegularShapeForm,
  // because the backend only need ids however for adHocShapes we need the GeoJson.
  if (
    !instantShape &&
    shapeItemData &&
    Object.values(shapeItemData).every(
      (item) => typeof item === 'object' && 'features' in item
    )
  ) {
    shapeItemData = convertGeoJsonShapeToStringId(
      shapeItemData as GeoJsonDictionary
    );
  }

  const defaultValues = useMemo(() => {
    const newDictionary: NewDictionary = {};
    if (type === 'multiShape') {
      addMultiShapeItemsToForm(
        shapeItems,
        newDictionary,
        shapeItemData as GeoJsonDictionary
      );
    } else {
      if (instantShapeData) {
        addItemsToForm(shapeItems, newDictionary, instantShapeData);
      } else {
        addItemsToForm(shapeItems, newDictionary, shapeItemData);
      }
    }
    return newDictionary;
  }, [shapeItemData, shapeItems, type, instantShapeData]);
  const form = useForm<ReportTemplateShapeValues>({
    resolver: zodResolver(shapeSchema),
    defaultValues: defaultValues,
  });
  return form;
}

function convertGeoJsonShapeToStringId(shapeDictionary: GeoJsonDictionary) {
  const newShapeDictionary: StringDictionary = {};
  Object.keys(shapeDictionary).map((key: string) => {
    newShapeDictionary[key] = shapeDictionary[key].properties.id || '';
  });
  return newShapeDictionary;
}
